import { figuresFor } from "../../api/figures";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Select, StatTile } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isoDate, ReportingClient, type Period } from "../../api/reporting";
import {
  partTotal,
  requestTotal,
  SERVICE_MOTIVE_COLUMNS,
  ServiceClient,
  type ProductServiceDTO,
  type ServiceMotiveDTO,
} from "../../api/service";

/* ------------------------------------------------------------- periods --- */

type PeriodMode = "MONTH" | "TRIMESTER" | "YEAR" | "ROLLING";
type Rolling =
  | "YTD"
  | "FOUR_WEEKS"
  | "TWELVE_WEEKS"
  | "FIFTY_TWO_WEEKS"
  | "LAST_MONTH"
  | "LAST_3_MONTHS"
  | "LAST_12_MONTHS";

function endOfMonth(year: number, month: number): string {
  return isoDate(new Date(year, month + 1, 0));
}

function resolvePeriod(
  mode: PeriodMode,
  year: number,
  month: number,
  trimester: number,
  rolling: Rolling,
): Period {
  const now = new Date();
  switch (mode) {
    case "MONTH":
      return { from: isoDate(new Date(year, month, 1)), to: endOfMonth(year, month) };
    case "TRIMESTER": {
      const start = (trimester - 1) * 3;
      return { from: isoDate(new Date(year, start, 1)), to: endOfMonth(year, start + 2) };
    }
    case "YEAR":
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    case "ROLLING": {
      const weeksBack = (weeks: number) => {
        const from = new Date(now);
        from.setDate(from.getDate() - weeks * 7);
        return { from: isoDate(from), to: isoDate(now) };
      };
      const monthsWindow = (back: number) => ({
        from: isoDate(new Date(now.getFullYear(), now.getMonth() - back, 1)),
        to: endOfMonth(now.getFullYear(), now.getMonth() - 1),
      });
      switch (rolling) {
        case "YTD":
          return { from: `${now.getFullYear()}-01-01`, to: isoDate(now) };
        case "FOUR_WEEKS":
          return weeksBack(4);
        case "TWELVE_WEEKS":
          return weeksBack(12);
        case "FIFTY_TWO_WEEKS":
          return weeksBack(52);
        case "LAST_MONTH":
          return monthsWindow(1);
        case "LAST_3_MONTHS":
          return monthsWindow(3);
        case "LAST_12_MONTHS":
          return monthsWindow(12);
      }
    }
  }
}

/* ---------------------------------------------------------------- page --- */

/** requests-per-unit-sold, with Vaadin's 100%-when-unsold grid fallback. */
function requestPercent(requests: number, unitsSold: number): number {
  if (unitsSold === 0) return requests === 0 ? 0 : 1;
  return requests / unitsSold;
}

function MotiveCell({
  requests,
  parts,
  percent,
  t,
}: {
  requests: number;
  parts: number;
  percent: number;
  t: (key: string, fallback: string) => string;
}) {
  if (requests === 0 && parts === 0) return <span className="text-ink-faint">0</span>;
  return (
    <span className="tabular">
      {requests}
      <span className="ml-1 text-[11px] text-ink-faint">
        {parts} {t("services.parts", "parts")} · {(percent * 100).toFixed(2)}%
      </span>
    </span>
  );
}

/**
 * Services: customer-service request volume per product, split by motive,
 * against units sold — with the four fixed-window KPI tiles Vaadin showed.
 */
export function ServicesPage() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();

  const service = useMemo(() => new ServiceClient(http), [http]);
  const reporting = useMemo(() => new ReportingClient(http), [http]);

  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>("ROLLING");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [trimester, setTrimester] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [rolling, setRolling] = useState<Rolling>("LAST_MONTH");

  const period = useMemo(
    () => resolvePeriod(mode, year, month, trimester, rolling),
    [mode, year, month, trimester, rolling],
  );

  const motivesQuery = useQuery({
    queryKey: ["service", "motives"],
    queryFn: () => service.motives(),
    staleTime: 30 * 60_000,
  });

  // The four KPI tiles use fixed windows, independent of the grid period.
  const kpiQuery = useQuery({
    queryKey: ["service", "kpi"],
    queryFn: async () => {
      const currentMonth: Period = {
        from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: endOfMonth(now.getFullYear(), now.getMonth()),
      };
      const lastMonth: Period = {
        from: isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: endOfMonth(now.getFullYear(), now.getMonth() - 1),
      };
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      const quarter: Period = {
        from: isoDate(new Date(now.getFullYear(), quarterStart, 1)),
        to: endOfMonth(now.getFullYear(), quarterStart + 2),
      };
      const ytd: Period = { from: `${now.getFullYear()}-01-01`, to: isoDate(now) };

      const windows = [
        { key: "current-month", label: t("services.kpi.current-month", "Current month"), period: currentMonth },
        { key: "last-month", label: t("services.kpi.last-month", "Last month"), period: lastMonth },
        { key: "quarter", label: t("services.kpi.quarter", "Current quarter"), period: quarter },
        { key: "ytd", label: t("services.kpi.ytd", "Current year"), period: ytd },
      ];
      return Promise.all(
        windows.map(async (window) => {
          const [totals, requests] = await Promise.all([
            reporting.salesTotals(window.period),
            service.requestLineCount(window.period),
          ]);
          // Pinned to billed units on purpose. This is the denominator of a
          // service-request rate, and a rate that moves when someone flips a
          // display toggle is not a rate. Named rather than re-derived.
          const unitsSold = figuresFor(totals, "all").units_billed;
          const percent = unitsSold === 0 ? 0 : requests / unitsSold;
          return { ...window, requests, unitsSold, percent };
        }),
      );
    },
    staleTime: 10 * 60_000,
  });

  const gridQuery = useQuery({
    queryKey: ["service", "grid", period.from, period.to],
    queryFn: async () => {
      const rows = await service.requestsByPeriod(period);
      const skus = rows.map((row) => row.product_sku ?? "").filter(Boolean);
      const partRows = skus.length ? await reporting.partRowsByParts(skus, period) : [];
      const unitsBySku = new Map<string, number>();
      for (const partRow of partRows) {
        const sku = partRow.part?.no;
        if (!sku) continue;
        unitsBySku.set(sku, (unitsBySku.get(sku) ?? 0) + (partRow.totals?.quantity ?? 0));
      }
      return { rows, unitsBySku };
    },
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ProductServiceDTO>();
    const unitsBySku = gridQuery.data?.unitsBySku ?? new Map<string, number>();
    const units = (row: ProductServiceDTO) => unitsBySku.get(row.product_sku ?? "") ?? 0;

    // One column per allow-listed motive, in Vaadin's order.
    const motives = (motivesQuery.data ?? []).filter(
      (motive): motive is ServiceMotiveDTO & { no: string } =>
        Boolean(motive.no) && SERVICE_MOTIVE_COLUMNS.includes(motive.no!),
    );

    return [
      c.text("product", (row) => row.product_sku ?? "", { headerKey: "column.product", priority: 1 }),
      c.number("units_sold", (row) => units(row), { headerKey: "column.sales.units", priority: 3 }),
      c.custom(
        "motive_total",
        (row) => requestTotal(row),
        (row) => (
          <MotiveCell
            requests={requestTotal(row)}
            parts={partTotal(row)}
            percent={requestPercent(requestTotal(row), units(row))}
            t={t}
          />
        ),
        { headerKey: "column.motive-total", filter: "none", priority: 2 },
      ),
      ...motives.map((motive) =>
        c.custom(
          `motive_${motive.no}`,
          (row) => requestTotal(row, motive.id),
          (row) => (
            <MotiveCell
              requests={requestTotal(row, motive.id)}
              parts={partTotal(row, motive.id)}
              percent={requestPercent(requestTotal(row, motive.id), units(row))}
              t={t}
            />
          ),
          { headerKey: motive.no, filter: "none" },
        ),
      ),
    ];
  }, [gridQuery.data, motivesQuery.data, t]);

  // Vaadin sorted by motive total descending.
  const rows = useMemo(
    () => [...(gridQuery.data?.rows ?? [])].sort((a, b) => requestTotal(b) - requestTotal(a)),
    [gridQuery.data],
  );

  const years = Array.from({ length: 3 }, (_, index) => now.getFullYear() - index);
  const months = Array.from({ length: 12 }, (_, index) => ({
    value: String(index),
    label: new Date(2000, index, 1).toLocaleString(undefined, { month: "long" }),
  }));

  return (
    <>
      <PageHeader
        title={t("navigation.item.services", "Services")}
        subtitle={t("services.subtitle", "Service requests per product, against units sold.")}
        badges={<Badge variant="neutral" size="sm">{`${period.from} → ${period.to}`}</Badge>}
      />

      <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4 lg:p-6">
        {(kpiQuery.data ?? []).map((kpi) => (
          <StatTile
            key={kpi.key}
            label={kpi.label}
            value={kpi.requests}
            note={`${(kpi.percent * 100).toFixed(2)}% ${t("services.of-units-sold", "of units sold")} (${kpi.unitsSold})`}
          />
        ))}
      </div>

      <DataTable
        toolbarExtra={
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="service-mode" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
              {t("margin.period-mode", "Period")}
              <Select
                id="service-mode"
                value={mode}
                options={[
                  { value: "MONTH", label: t("margin.mode.month", "Month") },
                  { value: "TRIMESTER", label: t("margin.mode.trimester", "Trimester") },
                  { value: "YEAR", label: t("margin.mode.year", "Year") },
                  { value: "ROLLING", label: t("margin.mode.rolling", "Rolling") },
                ]}
                onChange={(event) => setMode(event.target.value as PeriodMode)}
                className="h-8 w-32"
              />
            </label>
            {mode !== "ROLLING" && (
              <label htmlFor="service-year" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("analysis.year", "Year")}
                <Select
                  id="service-year"
                  value={String(year)}
                  options={years.map((value) => ({ value: String(value), label: String(value) }))}
                  onChange={(event) => setYear(Number(event.target.value))}
                  className="h-8 w-24"
                />
              </label>
            )}
            {mode === "MONTH" && (
              <label htmlFor="service-month" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("margin.mode.month", "Month")}
                <Select
                  id="service-month"
                  value={String(month)}
                  options={months}
                  onChange={(event) => setMonth(Number(event.target.value))}
                  className="h-8 w-36"
                />
              </label>
            )}
            {mode === "TRIMESTER" && (
              <label htmlFor="service-trimester" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("margin.mode.trimester", "Trimester")}
                <Select
                  id="service-trimester"
                  value={String(trimester)}
                  options={[1, 2, 3, 4].map((value) => ({ value: String(value), label: `T${value}` }))}
                  onChange={(event) => setTrimester(Number(event.target.value))}
                  className="h-8 w-20"
                />
              </label>
            )}
            {mode === "ROLLING" && (
              <label htmlFor="service-rolling" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("margin.window", "Window")}
                <Select
                  id="service-rolling"
                  value={rolling}
                  options={(
                    ["YTD", "FOUR_WEEKS", "TWELVE_WEEKS", "FIFTY_TWO_WEEKS", "LAST_MONTH", "LAST_3_MONTHS", "LAST_12_MONTHS"] as Rolling[]
                  ).map((value) => ({ value, label: value.replaceAll("_", " ") }))}
                  onChange={(event) => setRolling(event.target.value as Rolling)}
                  className="h-8 w-40"
                />
              </label>
            )}
          </div>
        }
        data={rows}
        columns={cols}
        getRowId={(row) => row.product_sku ?? ""}
        stateKey="/services"
        loading={gridQuery.isLoading}
        onRowClick={(row) =>
          row.product_sku &&
          void navigate({ to: "/services/$sku", params: { sku: row.product_sku } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
